import React from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
  Outlet,
  Navigate,
  useParams,
} from "react-router-dom";
import { useEffect } from "react";
import { jwtDecode } from "jwt-decode";
// Side-effect: instala o interceptor global de 401 no axios.
import "./js/axiosInterceptors.js";
import { AUTH_LOGOUT_EVENT } from "./js/axiosInterceptors.js";
import TCE from "./frontend/pages/TCE/index.js";
import TCA from "./frontend/pages/TCA/index.js";
import TcaCieemgEmpregador from "./frontend/pages/TcaEmpregador/index.js";
import Inicial from "./frontend/pages/Inicial/index.js";
import Dashboard from "./frontend/pages/Dashboard/index.js";
import Sidebar from "./frontend/components/Sidebar/index.js";
import Header from "./frontend/components/Header/index.js";
import TcaConfiguracoes from "./frontend/pages/TcaConfiguracoes/index.js";
import Acompanhamento from "./frontend/pages/Acompanhamento/index.js";
import Validacoes from "./frontend/pages/Validacoes/index.js";
import HistoricoEventos from "./frontend/pages/HistoricoEventos/index.js";
import ErroEmail from "./frontend/pages/ErroEmail/index.js";

// Escuta o evento global `auth:logout` (disparado pelo interceptor de
// axios quando QUALQUER request retorna 401) e redireciona para o
// login. Mantém o app inteiro consistente: não só Histórico, mas
// qualquer página (TCE, TCA, Acompanhamento, etc) é forçada a sair
// quando o token morre.
function AuthLogoutListener() {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = (ev) => {
      const reason = ev?.detail?.reason || "sessão expirada";
      try {
        window.localStorage.clear();
        window.sessionStorage.clear();
      } catch {
        /* storage indisponível */
      }
      console.warn("[App] auth:logout recebido — redirecionando. Motivo:", reason);
      navigate("/", { replace: true });
    };
    window.addEventListener(AUTH_LOGOUT_EVENT, handler);

    // Multi-aba: logout disparado em outra aba reflete imediatamente nesta
    const handleStorage = (e) => {
      if (e.key === "token" && !e.newValue) {
        try {
          window.sessionStorage.clear();
        } catch {}
        navigate("/", { replace: true });
      }
    };
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(AUTH_LOGOUT_EVENT, handler);
      window.removeEventListener("storage", handleStorage);
    };
  }, [navigate]);
  return null;
}


function AuthGuard({ children }) {
  const token = localStorage.getItem("token");
  const location = useLocation();

  // 1. Se não tem token nenhum, guarda a rota tentada e manda pro login
  if (!token) {
    if (location.pathname && location.pathname !== "/" && location.pathname !== "/login") {
      sessionStorage.setItem("redirectAfterLogin", location.pathname + location.search);
    }
    return <Navigate to="/" replace />;
  }
  try {
    const decoded = jwtDecode(token);
    // 2. Se o token tem data de expiração (exp em segundos) e ela já passou
    if (decoded.exp && decoded.exp * 1000 < Date.now()) {
      localStorage.clear();
      sessionStorage.clear();
      return <Navigate to="/" replace />;
    }
  } catch (e) {
    // 3. Se o token estiver corrompido, limpa tudo e manda pro login
    localStorage.clear();
    sessionStorage.clear();
    return <Navigate to="/" replace />;
  }
  return children;
}
       



function Layout() {
  const location = useLocation();
  const hideLayoutRoutes = ["/"];

  const hideLayout = hideLayoutRoutes.includes(location.pathname);

  return (
    <>
      {!hideLayout && <Header />}
      {!hideLayout && <Sidebar />}
      <Outlet />
    </>
  );
}

// Guard: empresa só acessa o programa dela
function EmpresaRouteGuard({ children, programaPermitido }) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const decoded = jwtDecode(token);

      let userObj = null;
      try {
        const rawUser = localStorage.getItem("user");
        if (rawUser) userObj = JSON.parse(rawUser);
      } catch (_) {}

      // Identificação ampla de Estudante (nível 1):
      // Verifica claims do JWT e também o objeto salvo no localStorage.
      const isEstudante =
        decoded.tipo === "estudante" ||
        userObj?.tipo === "estudante" ||
        Number(decoded.acesso_autorizacao ?? 0) === 1 ||
        Number(decoded.tipo_acesso ?? 0) === 1 ||
        Number(decoded.nivel_acesso ?? 0) === 1 ||
        Number(decoded.role_id ?? 0) === 1 ||
        Number(decoded.role ?? 0) === 1 ||
        Number(userObj?.acesso_autorizacao ?? 0) === 1 ||
        Number(userObj?.tipo_acesso ?? 0) === 1 ||
        Number(userObj?.nivel_acesso ?? 0) === 1 ||
        Number(userObj?.role_id ?? 0) === 1 ||
        Number(userObj?.role ?? 0) === 1;

      if (isEstudante) {
        // Se o estudante acessou /empresa/acompanhamento ou /acompanhamento COM ?id=... (ex.: por e-mail ou notificação),
        // ele deve ser direcionado para o formulário de preenchimento do contrato:
        const urlParams = new URLSearchParams(location.search);
        const idParam = urlParams.get("id");
        if (
          idParam &&
          (location.pathname.startsWith("/empresa/acompanhamento") ||
            location.pathname.startsWith("/acompanhamento"))
        ) {
          const tipoProg = Number(userObj?.tipo_programa ?? decoded.tipo_programa ?? 0);
          const rotaContrato =
            tipoProg === 2 ? "/tca-empregador" : tipoProg === 1 ? "/tca" : "/tce";
          navigate(`${rotaContrato}?id=${encodeURIComponent(idParam)}`, { replace: true });
          return;
        }

        // Rotas liberadas para estudante:
        // TCE, TCA, TCA-Empregador, Acompanhamento, Formulário, Estudante e Histórico.
        // Se o estudante clicou em um link direto com ?id=... (ex.: /tca-empregador?id=79 ou /tce?id=79),
        // ele DEVE permanecer no formulário da autorização para visualizar e preencher seus dados.
        const rotaLiberada =
          location.pathname.startsWith("/tce") ||
          location.pathname.startsWith("/tca") ||
          location.pathname.startsWith("/tca-empregador") ||
          location.pathname.startsWith("/acompanhamento") ||
          location.pathname.startsWith("/empresa/acompanhamento") ||
          location.pathname.startsWith("/formulario") ||
          location.pathname.startsWith("/estudante") ||
          location.pathname.startsWith("/historico-eventos");

        if (rotaLiberada) {
          return;
        }

        const mapaEstudante = {
          0: "/tce",
          1: "/tca",
          2: "/tca-empregador",
        };
        const tipoProg = Number(userObj?.tipo_programa ?? decoded.tipo_programa);
        const rotaDoPrograma = mapaEstudante[tipoProg] ?? null;
        navigate(rotaDoPrograma ?? "/acompanhamento", { replace: true });
        return;
      }

      const isEmpresa =
        !isEstudante &&
        (decoded.tipo === "empresa" ||
          decoded.tipo === "usuario_externo" ||
          userObj?.tipo === "empresa" ||
          userObj?.tipo === "usuario_externo");

      if (!isEmpresa) return;

      if (
        location.pathname.startsWith("/formulario") ||
        location.pathname.startsWith("/empresa/acompanhamento") ||
        location.pathname.startsWith("/acompanhamento") ||
        location.pathname.startsWith("/historico-eventos")
      ) {
        return;
      }

      // Se a empresa recebeu e-mail/notificação e clicou no link com ?id= (ex: /tca-empregador?id=79),
      // direciona para a aba de acompanhamento correspondente em vez de abrir o formulário
      const urlParams = new URLSearchParams(location.search);
      if (urlParams.get("id")) {
        navigate(`/empresa/acompanhamento${location.search}`, { replace: true });
        return;
      }

      const tipoPrograma = Number(decoded.tipo_programa);

      // Mapa de programas permitidos
      const mapa = {
        0: ["/tce"],
        1: ["/tca"],
        2: ["/tca-empregador"],
      };

      const permitidas = mapa[tipoPrograma] || [];
      if (permitidas.length === 0) return;

      if (!permitidas.some((p) => location.pathname.startsWith(p))) {
        navigate(permitidas[0], { replace: true });
      }
    } catch (e) {
      console.error("Erro no EmpresaRouteGuard:", e);
    }
  }, [location.pathname, location.search, navigate]);

  return children;
}

// Redirecionador dinâmico de formulário para links amigáveis (/formulario/:id, /formulario/preenchimento/:id, /estudante/:id, /estudante/preenchimento)
function FormularioRedirect() {
  const { id: paramId } = useParams();
  const location = useLocation();
  const searchId = new URLSearchParams(location.search).get("id");
  const id = paramId || searchId;
  const token = localStorage.getItem("token");
  if (!token) {
    return <Navigate to={`/?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  try {
    const decoded = jwtDecode(token);
    let userObj = null;
    try {
      const rawUser = localStorage.getItem("user");
      if (rawUser) userObj = JSON.parse(rawUser);
    } catch (_) {}

    const tipo = Number(userObj?.tipo_programa ?? decoded.tipo_programa ?? 0);
    const rota = tipo === 2 ? "/tca-empregador" : tipo === 1 ? "/tca" : "/tce";
    return <Navigate to={id ? `${rota}?id=${encodeURIComponent(id)}` : rota} replace />;
  } catch {
    return <Navigate to={id ? `/tce?id=${encodeURIComponent(id)}` : "/tce"} replace />;
  }
}

function App() {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AuthLogoutListener />
      <Routes>
        {/* Rota principal de Login */}
        <Route path="/" element={<Inicial />} />

        {/* Alias seguro: se alguém navegar para /login, redireciona para a raiz / */}
        <Route path="/login" element={<Navigate to="/" replace />} />

        {/* Todas as rotas internas protegidas pelo AuthGuard */}
        <Route
          element={
            <AuthGuard>
              <Layout />
            </AuthGuard>
          }
        >
          <Route
            path="/tce"
            element={
              <EmpresaRouteGuard programaPermitido={0}>
                <TCE />
              </EmpresaRouteGuard>
            }
          />
          <Route
            path="/tce/:id"
            element={
              <EmpresaRouteGuard programaPermitido={0}>
                <TCE />
              </EmpresaRouteGuard>
            }
          />
          <Route
            path="/tca"
            element={
              <EmpresaRouteGuard programaPermitido={1}>
                <TCA />
              </EmpresaRouteGuard>
            }
          />
          <Route
            path="/tca/:id"
            element={
              <EmpresaRouteGuard programaPermitido={1}>
                <TCA />
              </EmpresaRouteGuard>
            }
          />
          <Route
            path="/tca-empregador"
            element={
              <EmpresaRouteGuard programaPermitido={2}>
                <TcaCieemgEmpregador />
              </EmpresaRouteGuard>
            }
          />
          <Route
            path="/tca-empregador/:id"
            element={
              <EmpresaRouteGuard programaPermitido={2}>
                <TcaCieemgEmpregador />
              </EmpresaRouteGuard>
            }
          />
          {/* Rotas unificadas de formulário com parâmetro :id */}
          <Route path="/formulario/:id" element={<FormularioRedirect />} />
          <Route
            path="/formulario/preenchimento/:id"
            element={<FormularioRedirect />}
          />
          {/* Rotas amigáveis de notificação/e-mail para estudante */}
          <Route path="/estudante/:id" element={<FormularioRedirect />} />
          <Route path="/estudante/preenchimento" element={<FormularioRedirect />} />
          <Route
            path="/estudante/preenchimento/:id"
            element={<FormularioRedirect />}
          />

          <Route path="/tca-configuracoes" element={<TcaConfiguracoes />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/acompanhamento" element={<Acompanhamento />} />
          {/* Rota de acompanhamento direto da empresa */}
          <Route path="/empresa/acompanhamento" element={<Acompanhamento />} />
          <Route path="/validacoes" element={<Validacoes />} />
          <Route path="/historico-eventos" element={<HistoricoEventos />} />
          <Route path="/erro-email" element={<ErroEmail />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}


export default App;
